/**
 * Der Zähler-Bereich einer Anlage (HM-057, HM-058).
 *
 * **Der zweite Baum neben den Geräten.** Ein Gerät ist die Quelle, ein Zähler
 * ist das, was gezählt wird — und seit ADR 0020 hängen sie nur über die
 * Quellbindung am Zählwerk zusammen. Diese Liste ist deshalb kein zweiter Blick
 * auf die Geräteverwaltung: Sie zeigt, was an dieser Anlage gemessen wird, auch
 * wenn gerade kein Gerät daran hängt.
 *
 * **Die meisten Zähler entstehen aus einem Vorschlag** (HM-058). Liefert ein
 * Datenpunkt zum ersten Mal einen Wert, legt der Ingest eine Frage vor —
 * Zählwerke, Geräte-ID und ein Namensvorschlag inbegriffen. Bestätigt wird sie
 * hier, und **erst die Bestätigung legt an**: An einem Zähler hängt jede spätere
 * Abrechnung, und ob eine Messung als Zähler geführt werden soll, weiß nur, wer
 * vor dem Zählerschrank stand.
 *
 * Zwei Aufgaben hat diese Seite deshalb, und beide stehen oben: die offenen
 * **Vorschläge** und die Zähler, denen noch die **Zählernummer** fehlt. Beide
 * Abschnitte verschwinden, sobald sie erledigt sind.
 *
 * **„Zähler hinzufügen" ist für alles, was sich nicht meldet** — ein
 * Fremdgerät, eine Ablesung von Hand, ein Zähler vor dem Lesekopf. Der Knopf
 * steht nicht an erster Stelle: Wer ein eigenes Gerät koppelt, soll ihn nicht
 * brauchen.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  breakpoints,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Select,
  icons,
  useNarrow,
} from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { SectionHeading } from "../components/section.js";
import { navigate, routePath, type Route } from "../lib/router.js";
import { useMeterSuggestions, useMeteringPoints, useMeters } from "../lib/queries.js";
import { MeterSuggestions } from "../components/meter-suggestions.js";
import { boundRegisters, meterBadge, meterKindLabel, meterNumberText } from "../lib/meter-labels.js";
import { useSiteAccess } from "../lib/site-access.js";

type Meter = ApiComponents["schemas"]["Meter"];
type CreateMeterRequest = ApiComponents["schemas"]["CreateMeterRequest"];

const CLIP = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;
const SR_ONLY = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
} as const;

function meterRoute(siteId: string, meterId: string): Route {
  /* Auf die Übersicht, ohne Zählwerk und ohne Zeitraum: Die Liste kennt beide
     nicht, und die Detailseite nimmt dafür das erste Zählwerk und die üblichen
     24 Stunden. */
  return { kind: "meter", siteId, meterId, tab: "overview", register: null, range: "24h" };
}

export function MetersScreen({ siteId }: { siteId: string | null }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10).
  const rights = useSiteAccess();

  const meters = useMeters(siteId, includeInactive);
  const list = meters.data?.items ?? [];

  /**
   * **Die Vorschläge stehen oben, vor der Liste** (HM-058). Sie sind das, was
   * eine Entscheidung braucht; die Liste darunter ist Bestand. Wer ein Gerät
   * gerade gekoppelt hat, kommt hierher und findet die Frage als Erstes.
   */
  const suggestions = useMeterSuggestions(siteId);
  const open = suggestions.data?.items ?? [];

  /**
   * **Die Arbeitsliste, nicht eine Warnung.** Ein Zähler ohne Nummer ist der
   * planmäßige Zustand nach dem ersten Wert; der Kasten sagt, was zu tun ist,
   * und verschwindet danach. Er zählt nur, was gerade hängt — ein archivierter
   * Zähler ohne Nummer ist keine offene Aufgabe mehr.
   */
  const incomplete = list.filter(
    (meter) => meter.meterNumber === null && meter.status === "draft",
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 1100 }}>
      {siteId !== null && <MeterSuggestions siteId={siteId} suggestions={open} />}

      {rights.canWrite && incomplete.length > 0 && (
        <Alert
          tone="warning"
          /* Der Hinweis nennt den Zähler und führt zu ihm (UI-Durchsicht U8);
             bei mehreren steht der Weg in jeder Zeile der Liste darunter. */
          action={
            incomplete.length === 1 && siteId !== null ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(meterRoute(siteId, incomplete[0]!.id))}
              >
                Nummer eintragen …
              </Button>
            ) : undefined
          }
        >
          <strong>Zählernummer nachtragen.</strong>{" "}
          {incomplete.length === 1
            ? `„${incomplete[0]!.label}“ sammelt bereits Werte, hat aber noch keine Zählernummer.`
            : `${incomplete.map((meter) => `„${meter.label}“`).join(", ")} sammeln bereits Werte, haben aber noch keine Zählernummer.`}{" "}
          Sie steht auf dem Typenschild.
        </Alert>
      )}

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Zähler"
          subtitle="Was an dieser Anlage gemessen wird — mit den Zählwerken und ihren Quellen."
          action={
            rights.canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={siteId === null}
                onClick={() => setFormOpen(!formOpen)}
              >
                <icons.IconPlus size={15} />
                Zähler hinzufügen
              </Button>
            ) : undefined
          }
        />

        {rights.canWrite && formOpen && siteId !== null && (
          <MeterForm siteId={siteId} onDone={() => setFormOpen(false)} />
        )}

        <Card padding="none">
          {meters.isPending && <div style={{ padding: "var(--sp-4)" }}>Wird geladen …</div>}

          {/**
           * **Kein „leg einen an" als erster Satz.** Der übliche Weg zum
           * ersten Zähler führt über ein Gerät, das sendet — nicht über dieses
           * Formular. Wer hier steht, bevor ein Lesekopf gekoppelt ist, soll das
           * erfahren und nicht zum Nebenweg geschickt werden.
           */}
          {!meters.isPending && list.length === 0 && (
            <EmptyState title="Noch kein Zähler" icon={<icons.IconGauge size={30} />}>
              {rights.canWrite ? (
                <>
                  Sobald ein gekoppeltes Gerät den ersten Wert liefert, erscheint hier oben ein
                  Vorschlag — bestätigt wird er von dir. Für einen Zähler ohne eigenes Gerät gibt
                  es „Zähler hinzufügen".
                </>
              ) : (
                "An dieser Anlage ist noch kein Zähler angelegt."
              )}
            </EmptyState>
          )}

          {siteId !== null && list.length > 0 && <MeterTable siteId={siteId} meters={list} />}
        </Card>

        {/**
         * **Ausgeblendet und nicht weggelassen.** Ein abgelöster Zähler gehört
         * zur Geschichte dieser Anlage; er steht nur nicht zwischen denen, die
         * gerade hängen. Der Schalter steht unter der Liste, weil er sie
         * verändert und nicht sie beschreibt.
         */}
        <Checkbox
          checked={includeInactive}
          onChange={setIncludeInactive}
          label="Abgelöste und archivierte Zähler mitzeigen"
        />
      </section>
    </div>
  );
}

/**
 * Die Tabelle.
 *
 * Die Spalten sind die Fragen, die man an einer Zählerliste hat: Wie heißt er,
 * welche Nummer trägt er, was zählt er, kommt etwas an, in welchem Zustand ist
 * er. **Ein Momentanwert steht nicht dabei** — er käme aus den Zählwerksreihen,
 * und die gibt es erst mit HM-008. Eine Spalte, die überall „—" zeigt, ist eine
 * Zusage, die die Seite nicht hält.
 */
function MeterTable({ siteId, meters }: { siteId: string; meters: readonly Meter[] }) {
  /**
   * **Schmal bleiben zwei Spalten**, wie in der Geräteliste und wie im Kit.
   * Zählernummer, Bauart und die gebundenen Zählwerke rücken unter den Namen —
   * in **einer** Zeile, damit die Liste ihre Zeilenhöhe behält.
   */
  const narrow = useNarrow(breakpoints.table);
  const columns = narrow ? "minmax(0, 1fr) auto" : "1.6fr 1fr 1.1fr 1fr 0.9fr";

  return (
    <div role="table" aria-label="Zähler">
      <div
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: "var(--sp-3)",
          alignItems: "center",
          padding: "11px var(--sp-5)",
          borderBottom: "1px solid var(--border-subtle)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "var(--tracking-wide)",
          textTransform: "uppercase",
          color: "var(--text-faint)",
        }}
      >
        <span role="columnheader">Zähler</span>
        {!narrow && <span role="columnheader">Zählernummer</span>}
        {!narrow && <span role="columnheader">Bauart</span>}
        {!narrow && <span role="columnheader">Quellen</span>}
        <span role="columnheader">
          <span style={SR_ONLY}>Zustand</span>
        </span>
      </div>

      {meters.map((meter, index) => {
        const badge = meterBadge(meter);
        const bound = boundRegisters(meter);

        return (
          <div
            key={meter.id}
            role="row"
            style={{
              display: "grid",
              gridTemplateColumns: columns,
              gap: "var(--sp-3)",
              alignItems: "center",
              padding: "13px var(--sp-5)",
              borderBottom: index < meters.length - 1 ? "1px solid var(--border-subtle)" : "none",
              cursor: "pointer",
            }}
            /* Wie in der Geräteliste: Mausbequemlichkeit, kein zweites
               Bedienelement. Der bedienbare Teil ist der Link im Namen. */
            onClick={() => navigate(meterRoute(siteId, meter.id))}
            onMouseEnter={(event) => {
              event.currentTarget.style.background = "var(--surface-inset)";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = "transparent";
            }}
          >
            <div role="cell" style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-inset)",
                  color: "var(--text-muted)",
                }}
              >
                <icons.IconGauge size={17} />
              </span>
              <span style={{ minWidth: 0 }}>
                <a
                  href={routePath(meterRoute(siteId, meter.id))}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                      return;
                    }
                    event.preventDefault();
                    navigate(meterRoute(siteId, meter.id));
                  }}
                  style={{
                    display: "block",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--text-strong)",
                    textDecoration: "none",
                    ...CLIP,
                  }}
                >
                  {meter.label}
                </a>
                {/* Die Geräte-ID aus dem Telegramm — maschinell gelesen und
                    deshalb in der Monospace-Schrift, wie die Seriennummer eines
                    Geräts. Sie fehlt bei jedem Zähler, der von Hand angelegt
                    wurde, und dann steht dort nichts statt eines Platzhalters. */}
                {meter.serialFromDevice !== null && (
                  <span
                    style={{
                      display: "block",
                      fontFamily: "var(--font-mono)",
                      fontSize: 11.5,
                      color: "var(--text-muted)",
                      ...CLIP,
                    }}
                  >
                    {meter.serialFromDevice}
                  </span>
                )}
                {narrow && (
                  <span
                    style={{ display: "block", fontSize: 12, color: "var(--text-muted)", ...CLIP }}
                  >
                    {meterNumberText(meter)} · {meterKindLabel(meter.kind, meter.commodity)} ·{" "}
                    {bound} von {meter.registers.length} gebunden
                  </span>
                )}
              </span>
            </div>

            {!narrow && (
              <span role="cell" style={{ fontSize: 13, ...CLIP }}>
                {meter.meterNumber ?? (
                  <span style={{ color: "var(--text-faint)" }}>
                    {meter.virtual ? "virtuell" : meter.builtIntoDevice ? "Messwerk im Gerät" : "noch nicht eingetragen"}
                  </span>
                )}
              </span>
            )}

            {!narrow && (
              <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
                {meterKindLabel(meter.kind, meter.commodity)}
              </span>
            )}

            {/**
             * **Gebundene von allen**, nicht nur die Zahl der Zählwerke. Die
             * Frage an einem Zähler lautet, ob etwas ankommt — und „3 von 4"
             * beantwortet sie, wo „4 Zählwerke" nur zählt.
             */}
            {!narrow && (
              <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
                {bound} von {meter.registers.length}
              </span>
            )}

            <span role="cell">
              <Badge tone={badge.tone} title={badge.explanation}>
                {badge.label}
              </Badge>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Einen Zähler von Hand anlegen.
 *
 * **Die Messstelle ist die erste Frage, nicht die letzte.** Sie ist der Ort, an
 * dem nacheinander Zähler hängen — wer einen Nachfolger einträgt, muss ihn an
 * dieselbe hängen, sonst bricht die Historie an genau der Stelle, für die es
 * die Messstelle gibt.
 *
 * **Zählwerke werden über ihre Rolle gewählt, nicht über die Kennzahl.**
 * Einheit, Richtung und Monotonie folgen aus der Rolle; die Kennzahl setzt der
 * Server dazu. Wer sie zur Eingabe stellte, verlangte vom Nutzer eine Angabe,
 * die er nicht prüfen kann — und ein als „Einspeisung" angelegtes
 * Bezugszählwerk kehrt später jede Bilanz um.
 */
const ROLE_CHOICES = [
  { value: "energy_import_total", label: "Bezug gesamt (Zählerstand)" },
  { value: "energy_export_total", label: "Einspeisung gesamt (Zählerstand)" },
  { value: "energy_import_t1", label: "Bezug Tarif 1 (Zählerstand)" },
  { value: "energy_import_t2", label: "Bezug Tarif 2 (Zählerstand)" },
  { value: "power_total", label: "Momentanleistung" },
] as const;

function MeterForm({ siteId, onDone }: { siteId: string; onDone: () => void }) {
  const client = useQueryClient();
  const points = useMeteringPoints(siteId);

  const [label, setLabel] = useState("");
  const [meterNumber, setMeterNumber] = useState("");
  const [builtIntoDevice, setBuiltIntoDevice] = useState(false);
  /** `""` heißt „neue Messstelle" — der Regelfall beim ersten Zähler. */
  const [meteringPointId, setMeteringPointId] = useState("");
  const [roles, setRoles] = useState<readonly string[]>(["energy_import_total"]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (): Promise<Meter> => {
      const body: CreateMeterRequest = {
        siteId,
        label: label.trim(),
        kind: roles.includes("energy_export_total")
          ? roles.some((role) => role.startsWith("energy_import"))
            ? "two_way"
            : "generation"
          : "one_way",
        commodity: "electricity",
        builtIntoDevice,
        ...(meterNumber.trim().length > 0 ? { meterNumber: meterNumber.trim() } : {}),
        ...(meteringPointId.length > 0
          ? { meteringPointId }
          : { meteringPointLabel: label.trim() }),
        registers: roles.map((role) => ({ role: role as CreateMeterRequest["registers"][number]["role"] })),
      };

      const { data, error: failure } = await api.POST("/meters", { body });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Der Zähler ließ sich nicht anlegen.");
      }
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["meters"] });
      await client.invalidateQueries({ queryKey: ["metering-points"] });
      onDone();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  const valid = label.trim().length > 0 && roles.length > 0;

  return (
    <Card>
      <form
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: "var(--sp-3)",
          }}
        >
          <Input
            label="Bezeichnung"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Hauptzähler Keller"
            required
          />
          <Input
            label="Zählernummer"
            /* Ohne sie bleibt der Zähler ein Entwurf — dasselbe wie bei einem,
               der automatisch entstanden ist. Deshalb ist sie hier freiwillig:
               Wer gerade nicht am Zählerschrank steht, soll trotzdem anlegen
               können. */
            hint={
              builtIntoDevice
                ? "Ein Messwerk im Gerät hat keine — das Feld darf leer bleiben."
                : "Vom Typenschild. Kann später nachgetragen werden."
            }
            value={meterNumber}
            onChange={(event) => setMeterNumber(event.target.value)}
          />
          <Select
            label="Messstelle"
            value={meteringPointId}
            onChange={(event) => setMeteringPointId(event.target.value)}
            options={[
              { value: "", label: "Neue Messstelle anlegen" },
              ...(points.data?.items ?? []).map((point) => ({
                value: point.id,
                label: point.label,
              })),
            ]}
          />
        </div>

        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--text-muted)",
              padding: 0,
              marginBottom: "var(--sp-2)",
            }}
          >
            Zählwerke
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ROLE_CHOICES.map((choice) => (
              <Checkbox
                key={choice.value}
                checked={roles.includes(choice.value)}
                onChange={(checked) =>
                  setRoles((current) =>
                    checked
                      ? [...current, choice.value]
                      : current.filter((role) => role !== choice.value),
                  )
                }
                label={choice.label}
              />
            ))}
          </div>
        </fieldset>

        {/* HM-112: Der Energiezähler einer Wallbox oder Wärmepumpe hat kein Typenschild.
            Ohne das Merkmal bliebe er für immer ein Entwurf mit Nummern-Hinweis. */}
        <Checkbox
          checked={builtIntoDevice}
          onChange={setBuiltIntoDevice}
          label="Messwerk in einem Gerät, ohne Typenschild (Wallbox, Wärmepumpe, Wechselrichter)"
        />

        {error !== null && <Alert tone="danger">{error}</Alert>}

        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
          <Button type="submit" disabled={!valid || create.isPending}>
            {create.isPending ? "Wird angelegt …" : "Zähler anlegen"}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}
